import { CirclesNoiseFs, CirclesCursorFs, pixelsWithNoise, particleWithAbberationFs, alphaChannelFs, glitchPixelsWithNoise, mandelbrotFs, mirrorFs, lightGlowFs, matrixFs, matrixCursorFs, solidColor, lightGlowAndLayoutFs, layoutFs, wireframeCursorBoxPostProcessing, wireframeCursorEbbflowPostProcessing, mirrorCursorWithAbberationFs, mandelbrotCursorFs } from './shaders';
import imageScenesCMYK from './resources/scenes-CMYK.png'
import imageScenesPixels from './resources/scenes-Pixels.png'
import imageScenesMandelbrot from './resources/scenes-mandelbrot.png'
import imageScenesMirror from './resources/scenes-mirror.png'
import imageScenesEbbflow from './resources/scenes-ebbflow.png'
import imageScenesMatrix from './resources/scenes-matrix.jpg'
import imageScenesBoxes from './resources/scenes-Boxes.png'
import { ShapeInfo, boxCursor, ebbflowCursor, generateBoxes, generateSingleShape } from './geometryScenes';
import { matrixLayoutFunction, roundFrequencyVisualizer } from './samplerFunctions';
import { Sampler, StateType } from './artistWebgl';

export type SceneParameterType = 'toggle' | 'horizontalSlider' | 'verticalSlider';
export type SceneParameterCategory = 'global' | 'audio' | 'video';

export type SceneParameter = {
    name: string,
    value: number,
}

export type ParameterBox = {
    name: string,
    type: SceneParameterType,
    category: SceneParameterCategory,
    parameters: Array<SceneParameter>
}

export type SceneParameters = Array<ParameterBox>;

export type CursorParameters = {
    vertexShader?: string,
    fragmentShader?: string,
    postProcessing?: string,
    alphaFilteringRequired: boolean,
    samplers: Array<Sampler>,
    shapes?: () => Array<ShapeInfo>,
}

export type Scene = {
    id: number,
    title: string,
    description: string,
    image: string,
    audioReactive: boolean,
    interactive: boolean,
    videoResponsive: boolean,
    cameraRequired: boolean,
    vertexShader?: string,
    fragmentShader?: string,
    postProcessing?: string,
    fadeInTime: number,
    fadeInToNormalTransition: boolean,
    fadeOutTime: number,
    samplers: Array<Sampler>,
    parameters: SceneParameters | [],
    shapes?: () => Array<ShapeInfo>,
    cursor: CursorParameters,
}

export const scenes: Array<Scene> = [
    { 
        id: 0, 
        title: 'CMYK', 
        description: 'Content 0', 
        image: imageScenesCMYK, 
        audioReactive: false,
        interactive: true, 
        videoResponsive: true,
        cameraRequired: false,
        fragmentShader:CirclesNoiseFs, 
        fadeInTime: 3500, 
        fadeInToNormalTransition: false,
        fadeOutTime: 1000, 
        samplers: [
            {
                states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                sampleType: 'Camera',
                shapeSampler: false,
            }
        ],
        parameters: [{
            name: 'Pattern',
            type: 'horizontalSlider',
            category: 'global',
            parameters: [
                {
                    name: 'Pattern Size',
                    value: 0.125,
                },
                {
                    name: 'Rotation Speed',
                    value: 0,
                },
            ]
        },
        {
            name: 'Colors',
            type: 'toggle',
            category: 'global',
            parameters: [
                {
                    name: 'Cyan',
                    value: 1,
                },
                {
                    name: 'Magenta',
                    value: 1,
                },
                {
                    name: 'Yellow',
                    value: 1,
                },
                {
                    name: 'Black',
                    value: 1,
                },
            ]
        }],
        cursor: {
            fragmentShader: CirclesCursorFs, 
            alphaFilteringRequired: false,
            samplers: [],
        }
    },
    { 
        id: 1,
        title: 'Mirror',
        description: 'Content 1',
        image: imageScenesMirror,
        audioReactive: true,
        interactive: true,
        videoResponsive: true,
        cameraRequired: true,
        fragmentShader: mirrorFs,
        fadeInTime: 500,
        fadeInToNormalTransition: true,
        fadeOutTime: 500,
        samplers: [
            {
                states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                sampleType: 'Camera',
                shapeSampler: false,
            }
        ],
        parameters: [{
            name: 'General',
            type: 'horizontalSlider',
            category: 'global',
            parameters: [
                {
                    name: 'Mirror Type',
                    value: 0,
                },
                {
                    name: 'Color Filter',
                    value: 0.5,
                },
                {
                    name: 'Contrast',
                    value: 0.6,
                },
            ]
        },
        {
            name: 'Distortion',
            type: 'horizontalSlider',
            category: 'global',
            parameters: [
                {
                    name: 'Size',
                    value: 0.8,
                },
                {
                    name: 'Speed',
                    value: 0.2,
                },
                {
                    name: 'Displacement',
                    value: 0.6,
                },
            ]
        },
        ],
        cursor: {
            fragmentShader: mirrorCursorWithAbberationFs, 
            alphaFilteringRequired: true,
            samplers: [
                {
                    states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                    sampleType: 'Self',
                    shapeSampler: false,
                }
            ],
        }
    },
    { 
        id: 2, 
        title: 'Mandelbrot', 
        description: 'Content 2', 
        image: imageScenesMandelbrot, 
        audioReactive: true, 
        interactive: true, 
        videoResponsive: false,
        cameraRequired: false,
        fragmentShader: mandelbrotFs,  
        fadeInTime: 8000, 
        fadeInToNormalTransition: true,
        fadeOutTime: 2000,
        samplers: [],
        parameters: [{
            name: 'Location',
            type: 'horizontalSlider',
            category: 'global',
            parameters: [
                {
                    name: 'Location',
                    value: 0,
                },
            ]
        },
        {
            name: 'Colors',
            type: 'horizontalSlider',
            category: 'global',
            parameters: [
                {
                    name: 'Saturation',
                    value: 1,
                },
                {
                    name: 'Aberration',
                    value: 0,
                },
            ]
        },
        {
            name: 'Audioreactive',
            type: 'toggle',
            category: 'audio',
            parameters: [
                {
                    name: 'Saturation',
                    value: 1,
                },
                {
                    name: 'Rotation',
                    value: 0,
                },
                {
                    name: 'Aberration',
                    value: 0,
                },
                {
                    name: 'Zoom',
                    value: 0,
                },
            ]
        },
        ],
        cursor: {
            fragmentShader: mandelbrotCursorFs, 
            alphaFilteringRequired: false,
            samplers: [
                {
                    states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                    sampleType: 'Canvas',
                    shapeSampler: false,
                }
            ],
        }
    },
    { 
        id: 3, 
        title: 'Pixels', 
        description: 'Content 3', 
        image: imageScenesPixels, 
        audioReactive: false, 
        interactive: true, 
        videoResponsive: true,
        cameraRequired: false,
        fragmentShader: glitchPixelsWithNoise, 
        fadeInTime: 1000, 
        fadeInToNormalTransition: false,
        fadeOutTime: 4500, 
        samplers: [
            {
                states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                sampleType: 'Camera',
                shapeSampler: false,
            },
            {
                states: [StateType.FadeOut],
                sampleType: 'Self',
                shapeSampler: false,
            },
        ],
        parameters: [
            {
                name: 'Pixels',
                type: 'horizontalSlider',
                category: 'global',
                parameters: [
                    {
                        name: 'Size',
                        value: 0.3,
                    },
                ]
            },
        ],
        cursor: {
            fragmentShader: particleWithAbberationFs, 
            alphaFilteringRequired: true,
            samplers: [
                {
                    states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                    sampleType: 'Self',
                    shapeSampler: false,
                }
            ],
        }
    },
    { 
        id: 4, 
        title: 'Ebbflow', 
        description: 'Content 4', 
        image: imageScenesEbbflow, 
        audioReactive: true, 
        interactive: false, 
        videoResponsive: false,
        cameraRequired: false,
        fragmentShader: solidColor({r: 0, g: 0, b: 0}),
        postProcessing: lightGlowAndLayoutFs, 
        fadeInTime: 5000, 
        fadeInToNormalTransition: true,
        fadeOutTime: 3000,
        samplers: [],
        parameters: [{
            name: 'General',
            type: 'horizontalSlider',
            category: 'video',
            parameters: [
                {
                    name: 'Mirror Type',
                    value: 0,
                },
                {
                    name: 'Color Filter',
                    value: 0.5,
                },
                {
                    name: 'Contrast',
                    value: 0.6,
                },
            ]
        },
        {
            name: 'Distortion',
            type: 'horizontalSlider',
            category: 'video',
            parameters: [
                {
                    name: 'Size',
                    value: 0.8,
                },
                {
                    name: 'Speed',
                    value: 0.2,
                },
                {
                    name: 'Displacement',
                    value: 0.6,
                },
            ]
        },
        ],
        shapes: generateSingleShape,
        cursor: {
            fragmentShader: solidColor({r: 0,g: 0,b: 0}),
            postProcessing: wireframeCursorEbbflowPostProcessing,
            alphaFilteringRequired: false,
            samplers: [],
            shapes: ebbflowCursor,
        }
    },
    { 
        id: 5, 
        title: 'Matrix', 
        description: 'Content 5', 
        image: imageScenesMatrix, 
        audioReactive: false, 
        interactive: true, 
        videoResponsive: true,
        cameraRequired: true,
        fragmentShader: matrixFs,
        postProcessing: lightGlowFs,
        fadeInTime: 1000, 
        fadeInToNormalTransition: false,
        fadeOutTime: 5000,
        samplers: [
            {
                states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                sampleType: 'Camera',
                shapeSampler: false,
            },
            {
                states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                sampleType: 'Function',
                shapeSampler: false,
                samplerFunction: matrixLayoutFunction,
            },
            {
                states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                sampleType: 'BodySegmentation',
                shapeSampler: false,
            },
            {
                states: [StateType.FadeOut],
                sampleType: 'Self',
                shapeSampler: false,
            },
        ],
        parameters: [{
            name: 'Letters',
            type: 'horizontalSlider',
            category: 'global',
            parameters: [
                {
                    name: 'Size',
                    value: 0.2,
                }
            ]
        },
        {
            name: 'Video',
            type: 'horizontalSlider',
            category: 'video',
            parameters: [
                {
                    name: 'Contrast',
                    value: 0.5,
                },
            ]
        },
        {
            name: 'GPU-Intensive Features',
            type: 'toggle',
            category: 'video',
            parameters: [
                {
                    name: 'Body Tracking',
                    value: 0,
                },
            ]
        },
        ],
        cursor: {
            fragmentShader: matrixCursorFs, 
            postProcessing: lightGlowFs,
            alphaFilteringRequired: true,
            samplers: [
            {
                states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                sampleType: 'Function',
                shapeSampler: false,
                samplerFunction: matrixLayoutFunction,
            },
        ],
        }
    },
    { 
        id: 6, 
        title: 'Boxes', 
        description: 'Content 6', 
        image: imageScenesBoxes, 
        audioReactive: true, 
        interactive: false, 
        videoResponsive: false,
        cameraRequired: false,
        fragmentShader: solidColor({r: 0, g: 0, b: 0}),
        postProcessing: layoutFs, 
        fadeInTime: 3000, 
        fadeInToNormalTransition: true,
        fadeOutTime: 3000,
        samplers: [
            {
                states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                sampleType: 'Function',
                shapeSampler: true,
                shapeGroup: 1,
                samplerFunction: roundFrequencyVisualizer,
            },
        ],
        parameters: [{
            name: 'General',
            type: 'horizontalSlider',
            category: 'video',
            parameters: [
                {
                    name: 'Mirror Type',
                    value: 0,
                },
                {
                    name: 'Color Filter',
                    value: 0.5,
                },
                {
                    name: 'Contrast',
                    value: 0.6,
                },
            ]
        },
        {
            name: 'Distortion',
            type: 'horizontalSlider',
            category: 'video',
            parameters: [
                {
                    name: 'Size',
                    value: 0.8,
                },
                {
                    name: 'Speed',
                    value: 0.2,
                },
                {
                    name: 'Displacement',
                    value: 0.6,
                },
            ]
        },
        ],
        shapes: generateBoxes,
        cursor: {
            fragmentShader: solidColor({r: 0,g: 0,b: 0}),
            postProcessing: wireframeCursorBoxPostProcessing,
            alphaFilteringRequired: false,
            samplers: [],
            shapes: boxCursor,
        }
    },
];